import {
  type BackgroundTask,
  type BackgroundTaskState,
  useBackgroundTasksStore,
} from "@lib/stores/backgroundTasksStore";
import { useEffect, useRef } from "react";

/**
 * Calls `onSucceeded` each time a background task of `kind` reaches
 * `succeeded` while the component is mounted. Tasks that had already
 * succeeded before mount are ignored, so a page only refreshes for work that
 * finished while it was open (e.g. a LinkedIn import landing a new candidate).
 */
export function useBackgroundTaskCompletion(
  kind: string,
  onSucceeded: (task: BackgroundTask) => void,
) {
  const tasksById = useBackgroundTasksStore((s) => s.tasksById);
  const seen = useRef<Map<string, BackgroundTaskState> | null>(null);

  useEffect(() => {
    if (seen.current === null) {
      seen.current = new Map(Object.values(tasksById).map((t) => [t.id, t.state]));
      return;
    }
    for (const task of Object.values(tasksById)) {
      if (task.kind !== kind) continue;
      const previous = seen.current.get(task.id);
      seen.current.set(task.id, task.state);
      if (task.state === "succeeded" && previous !== "succeeded") onSucceeded(task);
    }
  }, [tasksById, kind, onSucceeded]);
}
